<script lang="ts">
  import type { Track } from '../../services/jitsi-service';
  import jitsiService from '../../services/jitsi-service';
  import { onDestroy, afterUpdate } from 'svelte';
  import logger from '../../utils/logger';

  export let track: Track;

  const CHECK_INTERVAL_MS = 5000;
  const PLAY_RETRY_MS = 750;

  let videoElement: HTMLVideoElement;
  let checkInterval: number;
  let playRetryTimeout: number;
  let attachedTrack: any = null;
  let recovering = false;
  let detaching = false;

  afterUpdate(() => {
    if (!videoElement || !track?.track) return;
    if (attachedTrack !== track.track) {
      attach(track.track);
    }
  });

  onDestroy(() => {
    detach();
    clearTimers();
    jitsiService.events.off('media_restored', onMediaRestored);
    jitsiService.events.off('remote_track_unmuted', onRemoteUnmuted);
    jitsiService.events.off('track_streaming_active', onStreamingActive);
  });

  jitsiService.events.on('media_restored', onMediaRestored);
  jitsiService.events.on('remote_track_unmuted', onRemoteUnmuted);
  jitsiService.events.on('track_streaming_active', onStreamingActive);

  function attach(jitsiTrack: any) {
    detach();
    attachedTrack = jitsiTrack;
    if (!videoElement || !jitsiTrack) return;

    jitsiTrack.attach(videoElement);
    logger.info('Attach video track');
    void playVideo('attach');
    startHealthCheck();
  }

  function detach() {
    detaching = true;
    if (attachedTrack && videoElement) {
      try {
        attachedTrack.detach(videoElement);
        logger.info('Detach video track');
      } catch (e) {
        logger.warn('Failed to detach video track', e as Error);
      }
    }
    attachedTrack = null;
    detaching = false;
  }

  function startHealthCheck() {
    if (checkInterval) {
      clearInterval(checkInterval);
    }
    checkInterval = window.setInterval(checkStream, CHECK_INTERVAL_MS);
  }

  function clearTimers() {
    if (checkInterval) {
      clearInterval(checkInterval);
      checkInterval = undefined;
    }
    if (playRetryTimeout) {
      clearTimeout(playRetryTimeout);
      playRetryTimeout = undefined;
    }
  }

  function diagnostics() {
    const jitsiTrack = track.track;
    const mediaStreamTrack = jitsiTrack?.getTrack?.();
    return {
      participantId: track.participantId,
      trackType: jitsiTrack?.getType?.(),
      jitsiTrackMuted: jitsiTrack?.isMuted?.(),
      jitsiTrackEnded: jitsiTrack?.isEnded?.(),
      mediaStreamTrackReadyState: mediaStreamTrack?.readyState,
      mediaStreamTrackEnabled: mediaStreamTrack?.enabled,
      mediaStreamTrackMuted: mediaStreamTrack?.muted,
      videoElementPaused: videoElement?.paused,
      videoElementReadyState: videoElement?.readyState,
      videoElementNetworkState: videoElement?.networkState,
      videoElementError: videoElement?.error
        ? { code: videoElement.error.code, message: videoElement.error.message }
        : null,
    };
  }

  async function playVideo(reason: string) {
    if (!videoElement) return;
    try {
      await videoElement.play();
    } catch (e) {
      logger.warn(`Video play() failed after ${reason}`, e as Error);
      if (playRetryTimeout) {
        clearTimeout(playRetryTimeout);
      }
      playRetryTimeout = window.setTimeout(() => {
        playRetryTimeout = undefined;
        if (videoElement?.paused) {
          void playVideo(`${reason}-retry`);
        }
      }, PLAY_RETRY_MS);
    }
  }

  async function recoverPlayback(reason: string) {
    if (recovering || !videoElement || !track?.track) return;
    recovering = true;
    try {
      const mediaStreamTrack = track.track.getTrack?.();
      if (mediaStreamTrack?.readyState && mediaStreamTrack.readyState !== 'live') {
        logger.warn(
          `Skip video recover (${reason}): media track is ${mediaStreamTrack.readyState}`
        );
        return;
      }

      logger.info(`Recovering video playback (${reason})`);
      await playVideo(reason);

      if (videoElement.paused || videoElement.readyState < 2) {
        const current = track.track;
        try {
          current.detach(videoElement);
        } catch (e) {
          // ignore
        }
        current.attach(videoElement);
        attachedTrack = current;
        await playVideo(`${reason}-reattach`);
      }
    } catch (e) {
      logger.error(
        `Video stream recover failed. Diagnostics: ${JSON.stringify(diagnostics())}`,
        e as Error
      );
    } finally {
      recovering = false;
    }
  }

  function checkStream() {
    if (!videoElement) return;
    if (!videoElement.paused && videoElement.readyState >= 2) return;

    const mediaStreamTrack = track.track?.getTrack?.();
    if (mediaStreamTrack?.readyState === 'ended') return;

    try {
      throw new Error(
        `Video element stream unexpectedly paused and could not be played. Diagnostics: ${JSON.stringify(
          diagnostics()
        )}`
      );
    } catch (e) {
      logger.error('Video stream unexpectedly paused.', e);
    }

    void recoverPlayback('health-check');
  }

  function onPause() {
    if (detaching || recovering) return;
    const mediaStreamTrack = track.track?.getTrack?.();
    if (mediaStreamTrack?.readyState === 'ended') return;
    void recoverPlayback('pause');
  }

  function onStalled() {
    void recoverPlayback('stalled');
  }

  function onMediaRestored() {
    void recoverPlayback('media-restored');
  }

  function onRemoteUnmuted(payload?: { participantId?: string; type?: string }) {
    if (payload?.participantId && payload.participantId !== track.participantId) return;
    if (payload?.type && payload.type !== 'video') return;
    void recoverPlayback('track-unmuted');
  }

  function onStreamingActive(payload?: { participantId?: string; type?: string }) {
    if (payload?.participantId && payload.participantId !== track.participantId) return;
    if (payload?.type && payload.type !== 'video') return;
    void recoverPlayback('streaming-active');
  }
</script>

<!-- svelte-ignore a11y-media-has-caption -->
<video
  bind:this={videoElement}
  autoplay
  muted
  playsinline
  class="video-container"
  id="video-{track.participantId}"
  on:pause={onPause}
  on:stalled={onStalled}
  on:suspend={onStalled}
  on:error={onStalled}
/>

<style>
  .video-container {
    width: 100%;
    height: 100vh;
    object-fit: cover;
    z-index: 2;
    position: absolute;
  }
</style>
